import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface Chemical {
  id: string;
  name: string;
  epaNumber: string;
  manufacturer: string;
  activeIngredient: string;
  signalWord: 'CAUTION' | 'WARNING' | 'DANGER';
  category: 'herbicide' | 'insecticide' | 'fungicide' | 'fertilizer' | 'other';
  formulation: string;
  rei: string; // Restricted Entry Interval
  phi: string; // Pre-Harvest Interval
  defaultRate: string;
  targetPests?: string[];
  crops?: string[];
  source: 'epa' | 'adn-recommended' | 'custom';
  // ADN-Recommended sponsor fields
  sponsorId?: string | null;
  sponsorName?: string | null;
  sponsorLogoUrl?: string | null;
  sponsorBrandColor?: string | null;
  sponsorWebsite?: string | null;
  sponsorPhone?: string | null;
  sponsorEmail?: string | null;
  isFeatured?: boolean;
  specialOffer?: string | null;
  // Additional links (admin-managed, user-visible)
  sdsUrl?: string | null; // Safety Data Sheet URL
  productUrl?: string | null; // Direct link to product on sponsor's site
  // EPA-specific fields
  currentLabelUrl?: string | null; // URL to current label PDF
  currentLabels?: Array<{url: string; name: string | null}> | null; // All current labels
  displayName?: string | null; // Primary custom name or product name
  epaAltBrandNames?: string | null; // Alternate brand names (semicolon-delimited)
}

export interface ChemicalSearchResult {
  chemicals: Chemical[];
  total: number;
  hasMore: boolean;
  page: number;
  totalPages: number;
  epaCount?: number;
  adnCount?: number;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  epaCount?: number;
  adnCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChemicalService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // Signal-based state management
  private chemicalsSignal = signal<Chemical[]>([]);
  private searchResultsSignal = signal<Chemical[]>([]);
  private isSearchingSignal = signal<boolean>(false);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private searchQuerySignal = signal<string>('');
  private categoriesSignal = signal<string[]>([]);
  private epaCountSignal = signal<number>(0);
  private adnCountSignal = signal<number>(0);
  private totalSignal = signal<number>(0);
  private currentPageSignal = signal<number>(1);
  private totalPagesSignal = signal<number>(1);

  // Public computed signals
  readonly chemicals = computed(() => this.chemicalsSignal());
  readonly searchResults = computed(() => this.searchResultsSignal());
  readonly isSearching = computed(() => this.isSearchingSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly searchQuery = computed(() => this.searchQuerySignal());
  readonly categories = computed(() => this.categoriesSignal());
  readonly epaCount = computed(() => this.epaCountSignal());
  readonly adnCount = computed(() => this.adnCountSignal());
  readonly total = computed(() => this.totalSignal());
  readonly currentPage = computed(() => this.currentPageSignal());
  readonly totalPages = computed(() => this.totalPagesSignal());

  constructor() {
    // Load categories on init
    this.loadCategories();
  }

  /**
   * Load categories from API
   */
  private async loadCategories(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/chemicals/categories`)
      );

      if (response.success && response.data) {
        this.categoriesSignal.set(response.data);
      }
    } catch (error: any) {
      console.error('Error loading categories:', error);
    }
  }

  /**
   * Search chemicals from API
   * GET /chemicals?q=&category=&source=&page=1&limit=25
   */
  async searchChemicals(
    query?: string,
    options: {
      category?: string;
      source?: string;
      page?: number;
      limit?: number;
    } = {}
  ): Promise<ChemicalSearchResult> {
    this.isSearchingSignal.set(true);
    this.errorSignal.set(null);

    if (query) {
      this.searchQuerySignal.set(query);
    }

    try {
      const params: any = {
        page: options.page || 1,
        limit: options.limit || 25
      };

      if (query && query.trim()) {
        params.q = query.trim();
      }
      if (options.category) params.category = options.category;
      if (options.source) params.source = options.source;

      const response = await firstValueFrom(
        this.http.get<ApiResponse<Chemical[]>>(`${this.apiUrl}/chemicals`, { params })
      );

      if (response.success && response.data) {
        const results: ChemicalSearchResult = {
          chemicals: response.data,
          total: response.total || 0,
          hasMore: (response.page || 1) < (response.totalPages || 1),
          page: response.page || 1,
          totalPages: response.totalPages || 1,
          epaCount: response.epaCount,
          adnCount: response.adnCount
        };

        this.searchResultsSignal.set(response.data);
        this.totalSignal.set(results.total);
        this.currentPageSignal.set(results.page);
        this.totalPagesSignal.set(results.totalPages);

        // Update counts if provided (on initial load)
        if (response.epaCount !== undefined) {
          this.epaCountSignal.set(response.epaCount);
        }
        if (response.adnCount !== undefined) {
          this.adnCountSignal.set(response.adnCount);
        }

        return results;
      }

      return { chemicals: [], total: 0, hasMore: false, page: 1, totalPages: 1 };
    } catch (error: any) {
      console.error('Error searching chemicals:', error);
      this.errorSignal.set(error.message || 'Failed to search chemicals');
      return { chemicals: [], total: 0, hasMore: false, page: 1, totalPages: 1 };
    } finally {
      this.isSearchingSignal.set(false);
    }
  }

  /**
   * Search chemicals without updating shared signals.
   * Use this for independent lookups (e.g., template form modal)
   * that should not affect the Chemical Book's state.
   */
  async searchChemicalsQuiet(
    query?: string,
    options: {
      category?: string;
      source?: 'epa' | 'adn-recommended' | 'custom';
      page?: number;
      limit?: number;
    } = {}
  ): Promise<ChemicalSearchResult> {
    try {
      const params: { q?: string; category?: string; source?: string; page: number; limit: number } = {
        page: options.page || 1,
        limit: options.limit || 25
      };

      if (query && query.trim()) {
        params.q = query.trim();
      }
      if (options.category) params.category = options.category;
      if (options.source) params.source = options.source;

      const response = await firstValueFrom(
        this.http.get<ApiResponse<Chemical[]>>(`${this.apiUrl}/chemicals`, { params })
      );

      if (response.success && response.data) {
        return {
          chemicals: response.data,
          total: response.total || 0,
          hasMore: (response.page || 1) < (response.totalPages || 1),
          page: response.page || 1,
          totalPages: response.totalPages || 1,
          epaCount: response.epaCount,
          adnCount: response.adnCount
        };
      }

      return { chemicals: [], total: 0, hasMore: false, page: 1, totalPages: 1 };
    } catch (error: any) {
      console.error('Error searching chemicals:', error);
      return { chemicals: [], total: 0, hasMore: false, page: 1, totalPages: 1 };
    }
  }

  /**
   * Get a chemical by ID from API
   */
  async getChemicalById(id: string): Promise<Chemical | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<Chemical>>(`${this.apiUrl}/chemicals/${id}`)
      );

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    } catch (error: any) {
      console.error('Error fetching chemical:', error);
      this.errorSignal.set(error.message || 'Failed to fetch chemical');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get chemicals filtered by source (client-side from search results)
   */
  getChemicalsBySource(source: 'epa' | 'adn-recommended' | 'custom'): Chemical[] {
    return this.searchResultsSignal().filter(c => c.source === source);
  }

  /**
   * Get featured ADN-recommended chemicals
   */
  getFeaturedChemicals(): Chemical[] {
    return this.searchResultsSignal().filter(c => c.isFeatured === true);
  }

  /**
   * Clear search results
   */
  clearSearch(): void {
    this.searchQuerySignal.set('');
    this.searchResultsSignal.set([]);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  // ============================================
  // Legacy mock chemical data below (for reference)
  // ============================================
  private loadMockChemicals_DEPRECATED(): void {
    // EPA/Book chemicals - larger set for realistic typeahead testing
    const bookChemicals: Chemical[] = [
      {
        id: 'chem-1',
        name: 'Roundup PowerMax',
        epaNumber: '524-549',
        manufacturer: 'Bayer',
        activeIngredient: 'Glyphosate',
        signalWord: 'CAUTION',
        category: 'herbicide',
        formulation: 'Soluble Concentrate',
        rei: '4 hours',
        phi: 'Pre-harvest',
        defaultRate: '22 oz/acre',
        targetPests: ['Broadleaf weeds', 'Grasses', 'Annual weeds'],
        crops: ['Corn', 'Soybeans', 'Cotton', 'Wheat'],
        source: 'epa'
      },
      {
        id: 'chem-2',
        name: 'Liberty 280 SL',
        epaNumber: '264-829',
        manufacturer: 'BASF',
        activeIngredient: 'Glufosinate-ammonium',
        signalWord: 'WARNING',
        category: 'herbicide',
        formulation: 'Soluble Liquid',
        rei: '12 hours',
        phi: '70 days',
        defaultRate: '29 oz/acre',
        targetPests: ['Broadleaf weeds', 'Grasses'],
        crops: ['Corn', 'Soybeans', 'Cotton'],
        source: 'epa'
      },
      {
        id: 'chem-3',
        name: 'Captan 80 WDG',
        epaNumber: '66330-35',
        manufacturer: 'Arysta LifeScience',
        activeIngredient: 'Captan',
        signalWord: 'DANGER',
        category: 'fungicide',
        formulation: 'Water Dispersible Granule',
        rei: '24 hours',
        phi: '0 days',
        defaultRate: '2.5 lb/acre',
        targetPests: ['Apple scab', 'Brown rot', 'Leaf spot'],
        crops: ['Apple Trees', 'Pear Trees', 'Cherries'],
        source: 'epa'
      },
      {
        id: 'chem-4',
        name: 'Prosaro 421 SC',
        epaNumber: '264-862',
        manufacturer: 'Bayer',
        activeIngredient: 'Prothioconazole + Tebuconazole',
        signalWord: 'CAUTION',
        category: 'fungicide',
        formulation: 'Suspension Concentrate',
        rei: '12 hours',
        phi: '30 days',
        defaultRate: '6.5 oz/acre',
        targetPests: ['Fusarium head blight', 'Leaf rust', 'Septoria'],
        crops: ['Wheat', 'Barley'],
        source: 'epa'
      },
      {
        id: 'chem-5',
        name: 'Atrazine 4L',
        epaNumber: '100-497',
        manufacturer: 'Syngenta',
        activeIngredient: 'Atrazine',
        signalWord: 'CAUTION',
        category: 'herbicide',
        formulation: 'Liquid',
        rei: '12 hours',
        phi: 'Pre-emergence',
        defaultRate: '1.5 qt/acre',
        targetPests: ['Broadleaf weeds', 'Grasses'],
        crops: ['Corn', 'Sorghum', 'Sugarcane'],
        source: 'epa'
      },
      {
        id: 'chem-6',
        name: 'Dicamba DGA',
        epaNumber: '7969-137',
        manufacturer: 'BASF',
        activeIngredient: 'Dicamba',
        signalWord: 'CAUTION',
        category: 'herbicide',
        formulation: 'Liquid',
        rei: '24 hours',
        phi: 'Pre-harvest',
        defaultRate: '12.8 oz/acre',
        targetPests: ['Broadleaf weeds', 'Pigweed', 'Waterhemp'],
        crops: ['Soybeans', 'Cotton'],
        source: 'epa'
      },
      {
        id: 'chem-7',
        name: 'Priaxor',
        epaNumber: '7969-327',
        manufacturer: 'BASF',
        activeIngredient: 'Fluxapyroxad + Pyraclostrobin',
        signalWord: 'CAUTION',
        category: 'fungicide',
        formulation: 'Suspension Concentrate',
        rei: '12 hours',
        phi: '21 days',
        defaultRate: '4 oz/acre',
        targetPests: ['Frogeye leaf spot', 'Brown spot', 'Rust'],
        crops: ['Soybeans', 'Peanuts'],
        source: 'epa'
      },
      {
        id: 'chem-8',
        name: 'Assail 30SG',
        epaNumber: '8033-36',
        manufacturer: 'UPL',
        activeIngredient: 'Acetamiprid',
        signalWord: 'CAUTION',
        category: 'insecticide',
        formulation: 'Soluble Granule',
        rei: '12 hours',
        phi: '7 days',
        defaultRate: '4 oz/acre',
        targetPests: ['Codling moth', 'Aphids', 'Leafrollers'],
        crops: ['Apple Trees', 'Pear Trees', 'Grapes'],
        source: 'epa'
      },
      {
        id: 'chem-9',
        name: 'Dual II Magnum',
        epaNumber: '100-816',
        manufacturer: 'Syngenta',
        activeIngredient: 'S-metolachlor',
        signalWord: 'CAUTION',
        category: 'herbicide',
        formulation: 'Emulsifiable Concentrate',
        rei: '24 hours',
        phi: 'Pre-emergence',
        defaultRate: '1.5 pt/acre',
        targetPests: ['Annual grasses', 'Small-seeded broadleaves'],
        crops: ['Corn', 'Soybeans', 'Cotton'],
        source: 'epa'
      },
      {
        id: 'chem-10',
        name: 'Warrior II',
        epaNumber: '100-1295',
        manufacturer: 'Syngenta',
        activeIngredient: 'Lambda-cyhalothrin',
        signalWord: 'WARNING',
        category: 'insecticide',
        formulation: 'Capsule Suspension',
        rei: '24 hours',
        phi: '21 days',
        defaultRate: '1.92 oz/acre',
        targetPests: ['Corn borers', 'Armyworms', 'Aphids'],
        crops: ['Corn', 'Soybeans', 'Wheat'],
        source: 'epa'
      },
      {
        id: 'chem-11',
        name: 'Trivapro',
        epaNumber: '100-1557',
        manufacturer: 'Syngenta',
        activeIngredient: 'Benzovindiflupyr + Azoxystrobin + Propiconazole',
        signalWord: 'CAUTION',
        category: 'fungicide',
        formulation: 'Suspension Concentrate',
        rei: '12 hours',
        phi: '14 days',
        defaultRate: '13.7 oz/acre',
        targetPests: ['Gray leaf spot', 'Northern corn leaf blight', 'Rust'],
        crops: ['Corn', 'Wheat'],
        source: 'epa'
      },
      {
        id: 'chem-12',
        name: 'Headline AMP',
        epaNumber: '7969-305',
        manufacturer: 'BASF',
        activeIngredient: 'Pyraclostrobin + Metconazole',
        signalWord: 'WARNING',
        category: 'fungicide',
        formulation: 'Emulsifiable Concentrate',
        rei: '12 hours',
        phi: '14 days',
        defaultRate: '10 oz/acre',
        targetPests: ['Gray leaf spot', 'Southern rust', 'Frogeye leaf spot'],
        crops: ['Corn', 'Soybeans', 'Wheat'],
        source: 'epa'
      },
      {
        id: 'chem-13',
        name: 'Intrepid 2F',
        epaNumber: '62719-442',
        manufacturer: 'Corteva',
        activeIngredient: 'Methoxyfenozide',
        signalWord: 'CAUTION',
        category: 'insecticide',
        formulation: 'Flowable',
        rei: '4 hours',
        phi: '14 days',
        defaultRate: '8 oz/acre',
        targetPests: ['Armyworms', 'Corn earworm', 'Loopers'],
        crops: ['Corn', 'Cotton', 'Soybeans'],
        source: 'epa'
      },
      {
        id: 'chem-14',
        name: 'Engenia',
        epaNumber: '7969-345',
        manufacturer: 'BASF',
        activeIngredient: 'Dicamba',
        signalWord: 'CAUTION',
        category: 'herbicide',
        formulation: 'Soluble Liquid',
        rei: '24 hours',
        phi: 'Pre-harvest',
        defaultRate: '12.8 oz/acre',
        targetPests: ['Broadleaf weeds', 'Palmer amaranth', 'Waterhemp'],
        crops: ['Soybeans', 'Cotton'],
        source: 'epa'
      },
      {
        id: 'chem-15',
        name: 'Cobra',
        epaNumber: '59639-35',
        manufacturer: 'Valent',
        activeIngredient: 'Lactofen',
        signalWord: 'WARNING',
        category: 'herbicide',
        formulation: 'Emulsifiable Concentrate',
        rei: '12 hours',
        phi: '45 days',
        defaultRate: '12.5 oz/acre',
        targetPests: ['Broadleaf weeds', 'Morning glory', 'Pigweed'],
        crops: ['Soybeans'],
        source: 'epa'
      }
    ];

    // ADN Recommended chemicals
    const adnRecommended: Chemical[] = [
      {
        id: 'adn-1',
        name: 'ADN Pro Fungicide',
        epaNumber: 'ADN-F-001',
        manufacturer: 'ADN Partners',
        activeIngredient: 'Azoxystrobin + Propiconazole',
        signalWord: 'CAUTION',
        category: 'fungicide',
        formulation: 'Suspension Concentrate',
        rei: '4 hours',
        phi: '14 days',
        defaultRate: '8 oz/acre',
        targetPests: ['Leaf spot', 'Rust', 'Powdery mildew'],
        crops: ['Corn', 'Soybeans', 'Wheat'],
        source: 'adn-recommended'
      },
      {
        id: 'adn-2',
        name: 'DroneGuard Adjuvant',
        epaNumber: 'ADN-A-002',
        manufacturer: 'ADN Partners',
        activeIngredient: 'Surfactant blend',
        signalWord: 'CAUTION',
        category: 'other',
        formulation: 'Liquid',
        rei: '4 hours',
        phi: '0 days',
        defaultRate: '0.25% v/v',
        crops: ['All crops'],
        source: 'adn-recommended'
      }
    ];

    // Custom chemicals
    const customChems: Chemical[] = [
      {
        id: 'custom-1',
        name: 'Custom Foliar Mix A',
        epaNumber: 'N/A',
        manufacturer: 'Custom Mix',
        activeIngredient: 'Micronutrient blend',
        signalWord: 'CAUTION',
        category: 'fertilizer',
        formulation: 'Liquid',
        rei: '4 hours',
        phi: '0 days',
        defaultRate: '1 gal/acre',
        crops: ['Corn', 'Soybeans'],
        source: 'custom'
      },
      {
        id: 'custom-2',
        name: 'Custom Micronutrient Blend',
        epaNumber: 'N/A',
        manufacturer: 'Custom Mix',
        activeIngredient: 'Zinc, Manganese, Boron',
        signalWord: 'CAUTION',
        category: 'fertilizer',
        formulation: 'Liquid',
        rei: '4 hours',
        phi: '0 days',
        defaultRate: '32 oz/acre',
        crops: ['Corn', 'Soybeans', 'Wheat'],
        source: 'custom'
      }
    ];

    // Deprecated - now using API
  }

  /**
   * Get category badge class for UI
   */
  getCategoryBadgeClass(category: string): string {
    switch (category) {
      case 'herbicide':
        return 'bg-success-subtle text-success';
      case 'insecticide':
        return 'bg-danger-subtle text-danger';
      case 'fungicide':
        return 'bg-primary-subtle text-primary';
      case 'fertilizer':
        return 'bg-warning-subtle text-warning-emphasis';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  /**
   * Get source badge class for UI
   */
  getSourceBadgeClass(source: string): string {
    switch (source) {
      case 'epa':
        return 'bg-primary-subtle text-primary';
      case 'adn-recommended':
        return 'bg-info-subtle text-info';
      case 'custom':
        return 'bg-warning-subtle text-warning-emphasis';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  /**
   * Get source display label
   */
  getSourceLabel(source: string): string {
    switch (source) {
      case 'epa':
        return 'EPA';
      case 'adn-recommended':
        return 'AA';
      case 'custom':
        return 'Custom';
      default:
        return source;
    }
  }
}
